import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { ArrowLeftRight, ArrowLeft } from 'lucide-react';
import SEOHead, { generateConverterStructuredData } from './SEOHead';
import {
  lengthUnits,
  getUnitById,
  convertLength,
  formatConversionTitle,
  generateSEODescription,
  popularLengthConversions,
} from '../data/lengthRoutes';

const UnitConversionPage = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLocalizedPath();
  const { conversion } = useParams();
  const [fromValue, setFromValue] = useState('1');
  const [toValue, setToValue] = useState('');

  // Parse conversion slug (e.g., "inches-to-cm" -> { from: "inch", to: "centimeter" })
  const parseConversion = (slug) => {
    if (!slug) return { fromId: null, toId: null };
    const parts = slug.split('-to-');
    if (parts.length !== 2) return { fromId: null, toId: null };
    return { fromId: parts[0], toId: parts[1] };
  };

  const { fromId, toId } = parseConversion(conversion);
  const fromUnit = getUnitById(fromId);
  const toUnit = getUnitById(toId);

  useEffect(() => {
    if (fromUnit && toUnit && fromValue) {
      const numValue = parseFloat(fromValue);
      if (!isNaN(numValue)) {
        const result = convertLength(numValue, fromUnit.id, toUnit.id);
        if (result !== null) {
          // Format the result
          if (Math.abs(result) < 0.000001 || Math.abs(result) > 1000000000) {
            setToValue(result.toExponential(6));
          } else {
            setToValue(parseFloat(result.toPrecision(10)).toString());
          }
        }
      }
    }
  }, [fromValue, fromUnit, toUnit]);

  const handleSwap = () => {
    if (fromUnit && toUnit) {
      window.location.href = `/convert/length/${toUnit.id}-to-${fromUnit.id}`;
    }
  };

  // Get related conversions (same from unit, different to units)
  const getRelatedConversions = () => {
    return lengthUnits
      .filter((u) => u.id !== fromId && u.id !== toId)
      .slice(0, 6)
      .map((u) => ({
        from: fromId,
        to: u.id,
        fromUnit: fromUnit,
        toUnit: u,
      }));
  };
  const relatedConversions = getRelatedConversions();

  // Get reverse and related popular conversions
  const otherPopularConversions = popularLengthConversions
    .filter((c) => !(c.from === fromId && c.to === toId))
    .slice(0, 8);

  // Generate structured data for SEO (must be before early return for hooks rules)
  const structuredData = useMemo(() => {
    if (!fromUnit || !toUnit) return null;
    return generateConverterStructuredData(fromUnit, toUnit, 'length');
  }, [fromUnit, toUnit]);

  // Generate keywords for this specific conversion
  const keywords = fromUnit && toUnit
    ? `${fromUnit.name} to ${toUnit.name}, convert ${fromUnit.plural} to ${toUnit.plural}, ${fromUnit.symbol} to ${toUnit.symbol}, ${fromUnit.name} ${toUnit.name} converter, length converter`
    : 'length converter, unit converter';

  if (!fromUnit || !toUnit) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <LocalizedLink
          to="/convert/length"
          className="flex items-center gap-2 text-[#2563eb] hover:text-[#1d4ed8] mb-4"
        >
          <ArrowLeft size={18} />
          {t('common.backToConverter', { category: t('sidebar.converters.length') })}
        </LocalizedLink>
        <p className="text-gray-600">{t('common.invalidConversion')}</p>
      </div>
    );
  }

  const fromUnitName = t(`lengthUnits.${fromUnit.id}.name`, fromUnit.name);
  const toUnitName = t(`lengthUnits.${toUnit.id}.name`, toUnit.name);
  const fromUnitPlural = t(`lengthUnits.${fromUnit.id}.plural`, fromUnit.plural);
  const toUnitPlural = t(`lengthUnits.${toUnit.id}.plural`, toUnit.plural);

  const pageTitle = t('seo.convertTitle', { from: fromUnitPlural, to: toUnitPlural });
  const pageDescription = t('seo.convertDescription', { from: fromUnitName, to: toUnitName });

  // Calculate conversion formula
  const conversionFactor = fromUnit.factor / toUnit.factor;
  const formulaText =
    conversionFactor >= 1
      ? `1 ${fromUnitName} = ${parseFloat(conversionFactor.toPrecision(10))} ${toUnitPlural}`
      : `1 ${fromUnitName} = ${conversionFactor.toExponential(6)} ${toUnitPlural}`;

  return (
    <div className="space-y-4">
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={keywords}
        structuredData={structuredData}
      />

      <LocalizedLink
        to="/convert/length"
        className="flex items-center gap-2 text-[#2563eb] hover:text-[#1d4ed8]"
      >
        <ArrowLeft size={18} />
        {t('common.backToConverter', { category: t('sidebar.converters.length') })}
      </LocalizedLink>

      {/* Main Converter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <h1 className="text-lg font-semibold text-white">{pageTitle}</h1>
        </div>

        <div className="p-6">
          <div className="grid md:grid-cols-[1fr,auto,1fr] gap-6 items-end">
            {/* From Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                {fromUnitPlural} ({fromUnit.symbol})
              </label>
              <input
                type="number"
                value={fromValue}
                onChange={(e) => setFromValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent text-lg"
                placeholder={t('common.enterValue')}
              />
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <button
                onClick={handleSwap}
                className="p-3 rounded-full bg-[#4a7c9b] text-white hover:bg-[#3d6a86] transition-colors shadow-md"
                title={t('common.swap')}
              >
                <ArrowLeftRight size={24} />
              </button>
            </div>

            {/* To Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                {toUnitPlural} ({toUnit.symbol})
              </label>
              <input
                type="text"
                value={toValue}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-lg"
                placeholder={t('common.result')}
              />
            </div>
          </div>

          {/* Result Display */}
          {fromValue && toValue && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-center text-lg text-gray-700">
                <span className="font-semibold">{fromValue}</span>{' '}
                <span className="text-gray-500">{fromUnitPlural}</span> ={' '}
                <span className="font-semibold text-[#4a7c9b] text-xl">{toValue}</span>{' '}
                <span className="text-gray-500">{toUnitPlural}</span>
              </p>
            </div>
          )}
        </div>

        {/* Conversion Formula */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">{t('common.formula')}</h2>
          <p className="text-sm text-gray-600">{formulaText}</p>
          <p className="text-sm text-gray-500 mt-1">
            {t('common.formulaDescription', { from: fromUnitPlural.toLowerCase(), to: toUnitPlural.toLowerCase(), factor: parseFloat(conversionFactor.toPrecision(6)) })}
          </p>
        </div>

        {/* Quick Reference Table */}
        <div className="border-t border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('common.quickReference')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    {fromUnitPlural} ({fromUnit.symbol})
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    {toUnitPlural} ({toUnit.symbol})
                  </th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 5, 10, 20, 50, 100, 500, 1000].map((val) => {
                  const result = convertLength(val, fromUnit.id, toUnit.id);
                  const formattedResult =
                    Math.abs(result) < 0.000001 || Math.abs(result) > 1000000000
                      ? result.toExponential(4)
                      : parseFloat(result.toPrecision(8)).toString();
                  return (
                    <tr key={val} className="border-t border-gray-100">
                      <td className="px-3 py-2">
                        {val} {fromUnit.symbol}
                      </td>
                      <td className="px-3 py-2 text-[#4a7c9b] font-medium">
                        {formattedResult} {toUnit.symbol}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Related Conversions */}
      
      {/* About This Conversion Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('common.unitDescriptionTitle')}</h2>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">{t('common.fromUnitLabel', { unit: t(`lengthUnits.${fromUnit.id}.name`, fromUnit.name) })}</h3>
            <p className="text-sm text-gray-600">{t(`lengthUnits.${fromUnit.id}.description`, t('common.noDescription'))}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">{t('common.toUnitLabel', { unit: t(`lengthUnits.${toUnit.id}.name`, toUnit.name) })}</h3>
            <p className="text-sm text-gray-600">{t(`lengthUnits.${toUnit.id}.description`, t('common.noDescription'))}</p>
          </div>
          <p className="text-sm text-gray-500 italic">{t('common.conversionNote', { from: t(`lengthUnits.${fromUnit.id}.plural`, fromUnit.plural), to: t(`lengthUnits.${toUnit.id}.plural`, toUnit.plural) })}</p>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('common.relatedConversions', { unit: fromUnitName })}</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {relatedConversions.map((conv, idx) => (
              <LocalizedLink
                key={idx}
                to={`/convert/length/${conv.from}-to-${conv.to}`}
                className="text-sm text-gray-600 hover:text-[#2563eb] transition-colors py-1"
              >
                {t('common.xToY', { from: t(`lengthUnits.${conv.fromUnit.id}.plural`, conv.fromUnit.plural), to: t(`lengthUnits.${conv.toUnit.id}.plural`, conv.toUnit.plural) })}
              </LocalizedLink>
            ))}
          </div>
        </div>
      </div>

      {/* Other Popular Conversions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('common.otherPopularConversions', { category: t('sidebar.converters.length') })}</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {otherPopularConversions.map((conv, idx) => {
              const from = getUnitById(conv.from);
              const to = getUnitById(conv.to);
              if (!from || !to) return null;
              return (
                <LocalizedLink
                  key={idx}
                  to={`/convert/length/${conv.from}-to-${conv.to}`}
                  className="text-sm text-gray-600 hover:text-[#2563eb] transition-colors py-1"
                >
                  {t('common.xToY', { from: t(`lengthUnits.${from.id}.plural`, from.plural), to: t(`lengthUnits.${to.id}.plural`, to.plural) })}
                </LocalizedLink>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitConversionPage;
